import { useState, useEffect } from 'react';
import {
  Radar, Plus, Trash2, RefreshCw, Loader2, AlertTriangle, ShieldCheck,
  ShieldAlert, Eye, Sparkles, ArrowLeft, Bot, BookOpen, ChevronDown,
  ChevronUp, Info,
} from 'lucide-react';
import FeatureGate from '../components/FeatureGate';
import {
  addCompetitor, listCompetitors, removeCompetitor, analyzeCompetitor,
} from '../services/api';
import { InfoTip } from '../components/ui';

const NETWORKS = [
  { id: 'ethereum',  label: 'Ethereum' },
  { id: 'base',      label: 'Base' },
  { id: 'arbitrum',  label: 'Arbitrum' },
  { id: 'optimism',  label: 'Optimism' },
  { id: 'polygon',   label: 'Polygon' },
  { id: 'bsc',       label: 'BNB Chain' },
  { id: 'avalanche', label: 'Avalanche' },
  { id: 'blast',     label: 'Blast' },
  { id: 'linea',     label: 'Linea' },
  { id: 'scroll',    label: 'Scroll' },
];

const HEALTH_COLOR = {
  excellent: 'text-emerald-400',
  good:      'text-cyan-300',
  fair:      'text-amber-300',
  poor:      'text-red-300',
  pending:   'text-gray-500',
};

const BOT_RATING = {
  clean:    { label: 'CLEAN',     icon: ShieldCheck,  cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  low:      { label: 'LOW',       icon: ShieldCheck,  cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  moderate: { label: 'MODERATE',  icon: ShieldAlert,  cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  high:     { label: 'HIGH',      icon: Bot,          cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

function fmtUsd(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  const n = Number(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function Hero({ onRefresh, refreshing, onAdd }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-indigo-500/20 p-6">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-indigo-400/10 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center flex-shrink-0">
            <Radar className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Competitor Tracker</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Real on-chain</span>
            </div>
            <p className="text-xs text-indigo-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Elite tier · Monitor competing tokens with live metrics + bot-likelihood detection
              <InfoTip side="bottom">Bot Likelihood is a HEURISTIC — every flag shows raw data so you can verify. Use as private DD, NOT public ammunition. Monthly cadence reveals momentum shifts.</InfoTip>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {onRefresh && (
            <button onClick={onRefresh} disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-400/30 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-50 text-xs">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          )}
          {onAdd && (
            <button onClick={onAdd} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-500 text-white hover:bg-indigo-400 text-xs font-bold">
              <Plus className="w-3.5 h-3.5" /> Track new token
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AboutCard({ open, onToggle }) {
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-indigo-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white">What is this tool — and how do I use it?</div>
            <div className="text-[11px] text-gray-500">Plain-English walkthrough · 30 seconds</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-indigo-300 uppercase tracking-wider mb-1.5">The problem</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              You need to know what your direct competitors are doing — liquidity, volume, holder distribution, suspicious activity. Checking each one manually across multiple explorers and aggregators takes hours and the data is scattered.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-indigo-300 uppercase tracking-wider mb-1.5">What this tool does</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Add competing tokens by contract address. We pull real on-chain metrics every 5 minutes — liquidity, 24h volume, buy/sell ratio, holder concentration — and overlay a <span className="text-white">Bot Likelihood score</span> that flags suspicious patterns like over-consistent volume, perfect 50/50 buy/sell, or daily volume that's 10× the pool depth. Every flag shows the raw data point so you can verify it yourself.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-indigo-300 uppercase tracking-wider mb-1.5">How to use it in 3 steps</div>
            <ol className="text-sm text-gray-300 leading-relaxed space-y-1.5 list-decimal pl-5">
              <li>Click <span className="text-white">Track new token</span>, paste a competitor's contract, pick its chain.</li>
              <li>The table shows liquidity, market cap, health, and bot likelihood at a glance. Click <span className="text-white">Details</span> on any row for the full breakdown.</li>
              <li>Use this monthly to spot wash-trading neighbors (so you can position credibly against them) or to find competitors that are losing momentum.</li>
            </ol>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold">How to read this:</span> Bot Likelihood is a heuristic, not a verdict. A high score means observable metrics LOOK suspicious — not that we've proven the token is fake. Use the flags as a starting point for your own due diligence, not a final judgment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function AddForm({ onSubmit, onCancel, loading }) {
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  const [network, setNetwork] = useState('ethereum');
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5 animate-fade-in">
      <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Add a token to track</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Display name (optional)</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Competitor Token"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-indigo-400/40 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Contract address</label>
          <input type="text" value={address} onChange={e => setAddress(e.target.value.trim())} placeholder="0x..."
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-indigo-400/40 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Network</label>
          <select value={network} onChange={e => setNetwork(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-indigo-400/40 focus:outline-none">
            {NETWORKS.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-xs text-gray-400 border border-dark-600 hover:text-white">Cancel</button>
        <button onClick={() => onSubmit({ tokenAddress: address, tokenName: name, network })}
          disabled={!address || !/^0x[a-fA-F0-9]{40}$/.test(address) || loading}
          className="px-4 py-2 rounded-lg text-xs font-bold bg-indigo-500 text-white hover:bg-indigo-400 disabled:opacity-40">
          {loading ? <Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 inline mr-1" />} Add
        </button>
      </div>
    </div>
  );
}

function CompetitorRow({ comp, onAnalyze, onRemove }) {
  const healthCol = HEALTH_COLOR[comp.healthRating] || HEALTH_COLOR.pending;
  const bot = comp.botLikelihood;
  const botRating = bot?.rating || 'clean';
  const botCfg = BOT_RATING[botRating] || BOT_RATING.clean;
  const BotIcon = botCfg.icon;
  return (
    <div className="bg-dark-800/60 rounded-xl border border-dark-700/50 hover:border-indigo-500/30 transition p-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-3 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-bold truncate">{comp.symbol || comp.tokenName || 'Unknown'}</h3>
            {comp.symbol && comp.symbol !== comp.tokenName && (
              <span className="text-[10px] text-gray-500 truncate">{comp.name || comp.tokenName}</span>
            )}
          </div>
          <p className="text-[10px] text-gray-500 font-mono truncate">{comp.tokenAddress}</p>
          <p className="text-[10px] text-gray-600 capitalize mt-0.5">{comp.network}</p>
        </div>
        <div className="md:col-span-2 text-center">
          <p className="text-[10px] uppercase text-gray-500">Market cap</p>
          <p className="text-sm font-mono text-white">{fmtUsd(comp.marketCapUsd)}</p>
        </div>
        <div className="md:col-span-2 text-center">
          <p className="text-[10px] uppercase text-gray-500">Health</p>
          {comp.healthScore != null ? (
            <p className={`text-sm font-mono font-bold ${healthCol}`}>
              {comp.healthScore} <span className="text-[9px] text-gray-500 font-normal uppercase">{comp.healthRating}</span>
            </p>
          ) : (
            <p className="text-sm text-gray-500">—</p>
          )}
        </div>
        <div className="md:col-span-3 text-center">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Bot likelihood</p>
          {bot?.score != null ? (
            <div className="inline-flex items-center gap-1.5 flex-wrap justify-center">
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${botCfg.cls}`}>
                <BotIcon className="w-3 h-3" /> {botCfg.label}
              </span>
              <span className="text-xs font-mono text-gray-400">{bot.score}/100</span>
              {bot.flags?.length > 0 && (
                <span className="text-[10px] text-gray-500">· {bot.flags.length} flag{bot.flags.length === 1 ? '' : 's'}</span>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500">insufficient data</p>
          )}
        </div>
        <div className="md:col-span-2 flex items-center gap-1.5 justify-end">
          <button onClick={() => onAnalyze(comp)}
            className="px-2 py-1.5 rounded-lg text-[11px] bg-dark-700 text-gray-300 hover:bg-dark-600 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Details
          </button>
          <button onClick={() => onRemove(comp.tokenAddress)}
            className="px-2 py-1.5 rounded-lg text-[11px] text-gray-600 hover:text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailView({ analysis, onBack, onRefresh, refreshing }) {
  if (!analysis) return null;
  const healthCol = HEALTH_COLOR[analysis.healthRating] || HEALTH_COLOR.pending;
  const bot = analysis.botLikelihood;
  const botRating = bot?.rating || 'clean';
  const botCfg = BOT_RATING[botRating] || BOT_RATING.clean;
  const BotIcon = botCfg.icon;

  // Plain-English summary inputs
  const highSevFlags = (bot?.flags || []).filter(f => f.severity === 'high');
  const topFlag = highSevFlags[0] || (bot?.flags || [])[0];
  const isClean = !bot?.flags || bot.flags.length === 0;
  const tokenLabel = analysis.symbol || analysis.name || 'This token';
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={onRefresh} disabled={refreshing}
          className="px-3 py-1.5 rounded-lg border border-indigo-400/30 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 text-xs disabled:opacity-40 flex items-center gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-white">{analysis.name || analysis.symbol || 'Token'} {analysis.symbol && analysis.symbol !== analysis.name ? `(${analysis.symbol})` : ''}</h2>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{analysis.tokenAddress} · {analysis.chain}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {bot && (
              <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase px-2.5 py-1 rounded-full border ${botCfg.cls}`}>
                <BotIcon className="w-3.5 h-3.5" /> {botCfg.label} ({bot.score}/100)
              </span>
            )}
            {analysis.healthScore != null && (
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full bg-dark-700/60 border border-dark-600 ${healthCol}`}>
                Health {analysis.healthScore}/100
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Stat label="Market cap" value={fmtUsd(analysis.marketCapUsd)} />
          <Stat label="Health rating" value={(analysis.healthRating || 'pending').toUpperCase()} colorClass={healthCol} />
          <Stat label="Bot signals" value={bot?.flags?.length ?? 0} subtext={bot?.confidence ? `${bot.confidence} confidence` : null} />
        </div>
      </div>

      {/* Plain-English summary */}
      {bot && bot.score != null && (
        <div className={`rounded-2xl p-5 border ${
          botRating === 'high' ? 'bg-gradient-to-br from-red-500/10 to-amber-500/5 border-red-500/30' :
          botRating === 'moderate' ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/30' :
          'bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/30'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 mt-0.5 ${
              botRating === 'high' ? 'bg-red-500/20 border-red-500/40' :
              botRating === 'moderate' ? 'bg-amber-500/20 border-amber-500/40' :
              'bg-emerald-500/20 border-emerald-500/40'
            }`}>
              <Sparkles className={`w-4 h-4 ${
                botRating === 'high' ? 'text-red-300' :
                botRating === 'moderate' ? 'text-amber-300' :
                'text-emerald-300'
              }`} />
            </div>
            <div className="space-y-2 flex-1">
              <h3 className="text-sm font-bold text-white">What this competitor looks like</h3>
              {isClean ? (
                <p className="text-xs text-gray-300 leading-relaxed">
                  {tokenLabel} shows <span className="text-emerald-300 font-semibold">clean</span> on-chain patterns — health score <span className="font-mono font-semibold">{analysis.healthScore ?? '—'}/100</span>, no bot-likelihood flags from the observable metrics. Treat them as a legitimate market competitor.
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    {tokenLabel} scores <span className="text-white font-mono font-semibold">{bot.score}/100</span> on bot likelihood ({botRating}), with <span className="text-white font-semibold">{bot.flags.length}</span> flag{bot.flags.length === 1 ? '' : 's'} detected. {highSevFlags.length > 0 && <>Of those, <span className="text-red-300 font-semibold">{highSevFlags.length}</span> {highSevFlags.length === 1 ? 'is' : 'are'} high severity.</>}
                  </p>
                  {topFlag && (
                    <p className="text-xs text-gray-300 leading-relaxed">
                      <span className="text-amber-300 font-semibold">Top concern:</span> <span className="text-white">{topFlag.type.replace(/-/g, ' ')}</span> — {topFlag.detail}
                    </p>
                  )}
                  <p className="text-[11px] text-gray-400 leading-relaxed">
                    This is a heuristic, not a verdict. Use these flags as a starting point for your own due diligence — verify the raw data points and decide whether the patterns are organic for this token's situation.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {bot?.flags && bot.flags.length > 0 && (
        <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Bot className="w-4 h-4 text-amber-400" /> Bot-likelihood signals
          </h3>
          <p className="text-[11px] text-gray-500 mb-3">
            Heuristic flags based on observable on-chain metrics. NOT a guarantee — every flag's data point is shown so you can verify.
          </p>
          <div className="space-y-2">
            {bot.flags.map((f, i) => {
              const sev = f.severity || 'low';
              const sevCls = sev === 'high'   ? 'bg-red-500/10 border-red-500/30 text-red-300'
                          : sev === 'medium' ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                                             : 'bg-gray-500/10 border-gray-500/30 text-gray-400';
              return (
                <div key={i} className={`rounded-lg p-3 border ${sevCls}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold uppercase">{f.type.replace(/-/g, ' ')}</span>
                    <span className="text-[10px] uppercase opacity-70">{sev}</span>
                    <span className="text-[10px] font-mono opacity-70 ml-auto">{f.source}</span>
                  </div>
                  <p className="text-[11px] leading-relaxed">{f.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {bot?.score === 0 && (!bot.flags || bot.flags.length === 0) && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
          <p className="text-sm text-emerald-300 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> {bot.message}
          </p>
        </div>
      )}

      {analysis.sources && (
        <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
          <h3 className="text-sm font-bold text-white mb-3">Data sources</h3>
          <div className="flex flex-wrap gap-2">
            <SourceBadge label="DEX Metrics" available={!!analysis.sources.dexMetrics} />
            <SourceBadge label="Market Data" available={!!analysis.sources.marketData} />
            <SourceBadge label="On-chain"    available={!!analysis.sources.onChain} />
          </div>
        </div>
      )}
    </div>
  );
}

function SourceBadge({ label, available }) {
  return (
    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${
      available
        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
        : 'bg-gray-500/15 text-gray-500 border-gray-500/30'
    }`}>
      {label} {available ? '✓' : '×'}
    </span>
  );
}

function Stat({ label, value, subtext, colorClass }) {
  return (
    <div className="bg-dark-700/30 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${colorClass || 'text-white'}`}>{value}</p>
      {subtext && <p className="text-[10px] text-gray-500 mt-0.5">{subtext}</p>}
    </div>
  );
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
      {message}
    </div>
  );
}

export default function CompetitorTrackerPage() {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState(null);
  const [detailAnalysis, setDetailAnalysis] = useState(null);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(true);

  async function loadCompetitors() {
    setRefreshing(true);
    try {
      const data = await listCompetitors();
      setCompetitors(data?.competitors || []);
    } catch { setCompetitors([]); }
    setLoading(false);
    setRefreshing(false);
  }
  useEffect(() => { loadCompetitors(); }, []);

  async function handleAdd({ tokenAddress, tokenName, network }) {
    setAdding(true);
    try {
      await addCompetitor(tokenAddress, tokenName, network);
      setToast({ message: 'Token added — fetching live metrics…', type: 'success' });
      setShowAdd(false);
      loadCompetitors();
    } catch (err) {
      setToast({ message: `Add failed: ${err.message}`, type: 'error' });
    }
    setAdding(false);
  }
  async function handleRemove(addr) {
    if (!window.confirm('Remove this competitor?')) return;
    try {
      await removeCompetitor(addr);
      setToast({ message: 'Removed', type: 'success' });
      loadCompetitors();
    } catch (err) {
      setToast({ message: `Remove failed: ${err.message}`, type: 'error' });
    }
  }
  async function handleAnalyze(comp) {
    setDetailRefreshing(true);
    try {
      const a = await analyzeCompetitor(comp.tokenAddress, comp.network);
      setDetailAnalysis(a);
    } catch (err) {
      setToast({ message: `Analyze failed: ${err.message}`, type: 'error' });
    } finally {
      setDetailRefreshing(false);
    }
  }

  if (detailAnalysis) {
    return (
      <FeatureGate feature="competitorTracker">
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
          <Hero />
          <DetailView
            analysis={detailAnalysis}
            onBack={() => setDetailAnalysis(null)}
            onRefresh={() => handleAnalyze({ tokenAddress: detailAnalysis.tokenAddress, network: detailAnalysis.chain })}
            refreshing={detailRefreshing}
          />
          {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
        </div>
      </FeatureGate>
    );
  }

  return (
    <FeatureGate feature="competitorTracker">
      <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
        <Hero onRefresh={loadCompetitors} refreshing={refreshing} onAdd={() => setShowAdd(true)} />

        <AboutCard open={aboutOpen} onToggle={() => setAboutOpen(o => !o)} />

        {showAdd && (
          <AddForm onSubmit={handleAdd} onCancel={() => setShowAdd(false)} loading={adding} />
        )}

        {loading && (
          <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 p-10 text-center">
            <Loader2 className="w-6 h-6 text-indigo-400 mx-auto animate-spin" />
          </div>
        )}

        {!loading && competitors.length === 0 && !showAdd && (
          <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 border-dashed p-12 text-center">
            <Radar className="w-12 h-12 text-indigo-400/40 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-1">Track your competition</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
              Add competing tokens to monitor their liquidity, volume, holder distribution, and bot-likelihood — all from real on-chain data.
            </p>
            <button onClick={() => setShowAdd(true)} className="px-5 py-2.5 rounded-xl bg-indigo-500 text-white text-sm font-bold hover:bg-indigo-400">
              <Plus className="w-4 h-4 inline mr-1" /> Add your first competitor
            </button>
          </div>
        )}

        {!loading && competitors.length > 0 && (
          <>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{competitors.length} token{competitors.length !== 1 ? 's' : ''} tracked · scores refresh every 5 min</span>
            </div>
            <div className="space-y-2">
              {competitors.map(c => (
                <CompetitorRow
                  key={c.tokenAddress + ':' + c.network}
                  comp={c}
                  onAnalyze={handleAnalyze}
                  onRemove={handleRemove}
                />
              ))}
            </div>

            <div className="bg-dark-900/40 border border-dark-700/40 rounded-xl p-4 text-[11px] text-gray-500 leading-relaxed">
              <span className="font-bold text-gray-400">How this works:</span> Each token is scored from real public data — live DEX metrics (liquidity + 24h transactions + buy/sell ratio), market data (30-day volume variance + market cap), and on-chain holder distribution (top-10 concentration). The Bot Likelihood score is a heuristic that flags suspicious patterns; every flag carries its underlying data point so you can verify. We don't fabricate metrics — if a source is unavailable, the row says so.
            </div>
          </>
        )}

        {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      </div>
    </FeatureGate>
  );
}
